import React from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'

import { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box/Box'

import { isClosePositionEffect, isOpenPositionEffect } from '../../utils/DxOrder'
import { TradeDetailsHistoryModal } from '../Modals/TradeDetailsHistoryModal'
import type { HistoryOrderWithLots } from '../hooks/useHistoryOrdersWithLots'
import { useOrderNetPnl } from '../hooks/useOrderNetPnl'
import type { OvernightFeesMap } from '../utils/DxtradeCashTransfersFields'
import { formatSymbol, renderPnl, renderSide } from '../utils/formatters'
import { TradeCardList } from './TradeCardList'
import { TradeCardRow } from './TradeCardRow'

import styles from './CardTradingView.module.scss'

const sx: Record<string, string> = styles as unknown as Record<string, string>

interface Props {
  orders: HistoryOrderWithLots[]
  isLoading: boolean
  isLoadingMore?: boolean
  accountCurrency?: CurrencyType
  overnightFeesMap?: OvernightFeesMap
  hasActiveFilters?: boolean
}

interface HistoryCardProps {
  order: HistoryOrderWithLots
  accountCurrency?: CurrencyType
  overnightFeesMap?: OvernightFeesMap
}

const HistoryCard: React.FC<HistoryCardProps> = ({ order, accountCurrency, overnightFeesMap }) => {
  const detailsModal = useModal(TradeDetailsHistoryModal)
  const { t } = useTranslation()

  const sizeLots = order.sizeLots
  const positionEffect = order.legs?.[0]?.positionEffect
  const positionCode = order.legs?.[0]?.positionCode
  const overnightFees =
    isClosePositionEffect(positionEffect) && positionCode
      ? overnightFeesMap?.get(positionCode) ?? null
      : null
  const positionEffectLabel = isOpenPositionEffect(positionEffect)
    ? t('Portfolio.Opened')
    : t('Portfolio.Closed')
  const netPnl = useOrderNetPnl(order, overnightFees)

  return (
    <TradeCardRow
      className={sx.pendingRow}
      onClick={() => detailsModal.open({ order, accountCurrency, overnightFees })}
      sideLabel={formatSymbol(order.instrument, order.description)}
      rightPrimary={
        isClosePositionEffect(positionEffect) ? (
          <SensitiveData>{renderPnl(netPnl, Body1, accountCurrency)}</SensitiveData>
        ) : null
      }
      caption={
        <Box flex justify='space-between' align='center'>
          <Box flex gap='x8' align='center'>
            {renderSide(order.side, Body2)}
            <Caption1 color='text-med-em'>
              <SensitiveData>{sizeLots ?? '-'}</SensitiveData>
            </Caption1>
          </Box>
          <Caption1 color='text-med-em'>{positionEffectLabel}</Caption1>
        </Box>
      }
      actionsRight={null}
    />
  )
}

export const CardHistoryView: React.FC<Props> = ({
  orders,
  isLoading,
  isLoadingMore,
  accountCurrency,
  overnightFeesMap,
  hasActiveFilters,
}) => {
  return (
    <TradeCardList
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      isEmpty={orders.length === 0}
      hasActiveFilters={hasActiveFilters}
    >
      {orders.map((order) => (
        <HistoryCard
          key={order.orderId}
          order={order}
          accountCurrency={accountCurrency}
          overnightFeesMap={overnightFeesMap}
        />
      ))}
    </TradeCardList>
  )
}

export default CardHistoryView
